import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { ArrowLeft, ChevronRight, DollarSign, PlusCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import SkinsEventDetail from '../components/SkinsEventDetail';
import { SkinsErrorBoundary } from '../components/skins/SkinsErrorBoundary';
import { apiError } from '../utils/apiError';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SkinsPage({ user }) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const selectedEvent = params.get('event');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const creatingRef = useRef(false);
  const fetchEvents = useCallback(async () => {
    setError('');
    try {
      const { data } = await axios.get(`${API}/skins/events`, { timeout: 20000 });
      if (!Array.isArray(data.events)) throw new Error('Invalid game list');
      setEvents(data.events.filter(event => event?.event_id));
    } catch (err) { setError(apiError(err, 'Unable to load games. Please try again.')); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { if (!selectedEvent) fetchEvents(); }, [selectedEvent, fetchEvents]);
  const backToGames = () => setParams({});
  const createGame = async () => {
    if (creatingRef.current) return;
    creatingRef.current = true;
    setCreating(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/skins/events`, {
        name: 'Skin Raiders', date: format(new Date(), 'yyyy-MM-dd'), holes: 18,
        skin_value: 0, use_handicaps: false, has_prize_pool: false, entry_fee: 0
      }, { timeout: 30000 });
      if (!data.event_id) throw new Error('Missing event');
      setParams({ event: data.event_id });
    } catch (err) { setError(apiError(err, 'Unable to create game. Check your games before retrying.')); }
    finally { creatingRef.current = false; setCreating(false); }
  };
  if (selectedEvent) return <SkinsErrorBoundary key={selectedEvent} onBack={backToGames}>
    <SkinsEventDetail eventId={selectedEvent} onBack={backToGames} user={user} />
  </SkinsErrorBoundary>;
  return <div className="ps-theme-page dark-page max-w-4xl mx-auto p-4" data-testid="skins-page">
    <header className="flex flex-wrap items-center gap-3 mb-6">
      <Button variant="ghost" data-testid="skins-back-to-compete" aria-label="Back to Compete" onClick={() => navigate('/tournaments')}><ArrowLeft /></Button>
      <h1 className="text-xl font-bold text-amber-500 flex-1" data-testid="skins-page-title">SKINS GAMES</h1>
      <Button data-testid="skins-create-game" disabled={creating} onClick={createGame} className="bg-amber-600 text-white hover:bg-amber-700">
        <PlusCircle className="w-4 h-4 mr-2" />{creating ? 'Creating…' : 'Create Game'}
      </Button>
    </header>
    {error && <div role="alert" className="mb-4 space-y-2" data-testid="skins-list-error">
      <p>{error}</p><Button variant="outline" data-testid="skins-list-retry" onClick={fetchEvents}>Refresh games</Button>
    </div>}
    {loading ? <p role="status" data-testid="skins-list-loading">Loading games…</p> :
      !events.length ? <p data-testid="skins-list-empty">No Skins games yet.</p> :
      <div className="space-y-3">{events.map(event => <button type="button" key={event.event_id}
        data-testid={`skins-open-${event.event_id}`} onClick={() => setParams({ event: event.event_id })}
        className="dark-card w-full text-left p-4 flex items-center gap-3 hover:border-amber-500/50 transition-colors">
        <DollarSign className="w-6 h-6 text-amber-500 shrink-0" />
        <span className="flex-1 min-w-0"><span className="font-semibold block break-words">{event.name}</span>
          <span className="text-sm text-[var(--ps-text-secondary)]" data-testid={`skins-summary-${event.event_id}`}>{event.player_count} players · {event.holes || 18} holes · {event.date}</span></span>
        <span className="text-xs" data-testid={`skins-status-${event.event_id}`}>{event.legacy_read_only ? 'Legacy' : event.status === 'active' ? 'In Progress' : event.status === 'completed' ? 'Completed' : 'Setup'}</span>
        <ChevronRight className="w-4 h-4 shrink-0" />
      </button>)}</div>}
  </div>;
}